import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { ArrowPathIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import classes from "./classes.module.scss";
import I18n from "components/materials/I18n";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import FileService from "services/FileService";
import { container } from "tsyringe";
import LazyImage from "components/elements/LazyImage";
import ProjectService from "services/ProjectService";
import { EPosterStatus } from "common/enums/Project/Poster/EPosterStatus";
import { match } from "ts-pattern";
import Typography from "components/elements/Typography";
import AnimatedLoader from "components/elements/AnimatedLoader";

const fileService = container.resolve(FileService);
const projectService = container.resolve(ProjectService);

type IProps = {
	project: ProjectResponseResource | null;
	onPosterChange: () => void;
};

function PosterContainer(props: IProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [newPosterFile, setNewPosterFile] = useState<File | null>(null);
	const [posterUrl, setPosterUrl] = useState<string | null>(null);

	const fetchPosterUrl = useCallback(async () => {
		const fileId = props.project?.poster?.file?.id;
		if (!fileId) return setPosterUrl(null);
		const url = fileService.getFileUrl("POSTER", fileId);
		setPosterUrl(url);
	}, [props.project]);

	const onRegeneratePosterButtonClick = useCallback(() => {
		if (!props.project) return;
		projectService.changeGeneratedPosterImage(props.project.id).then(props.onPosterChange).catch(console.error);
	}, [props.project, props.onPosterChange]);

	const onUploadPosterButtonClick = useCallback(() => {
		inputRef.current?.click();
	}, []);

	const onPosterFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		setNewPosterFile(file);
	}, []);

	const posterMessage = useMemo(() => {
		const status = props.project?.poster?.status;
		return match(status)
			.with(EPosterStatus.PENDING, () => I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.poster.pending))
			.with(EPosterStatus.FAILED, () => I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.poster.failed))
			.otherwise(() => I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.poster.pending));
	}, [props.project]);

	useEffect(() => {
		fetchPosterUrl();
	}, [fetchPosterUrl]);

	return (
		<div className={classes["root"]}>
			<div className={classes["poster"]}>
				{newPosterFile && <LazyImage src={URL.createObjectURL(newPosterFile)} alt="" />}
				{posterUrl && !newPosterFile && <LazyImage src={posterUrl} alt="" />}
				{!posterUrl && !newPosterFile && (
					<div className={classes["missing-poster"]}>
						<Typography color="secondary">{posterMessage}</Typography>
						{props.project?.poster?.status !== EPosterStatus.FAILED && <AnimatedLoader />}
					</div>
				)}
			</div>
			<div className={classes["buttons-container"]}>
				<Button
					startIcon={<ArrowPathIcon className={classes["icon"]} />}
					variant={EButtonVariant.TEXT}
					color={EButtonColor.PRIMARY}
					fullwidth
					onClick={onRegeneratePosterButtonClick}
					className={classes["button"]}>
					{I18n.asset.pages.professional_space.tabs.information.poster_container.buttons.regenerate}
				</Button>
				<Button
					startIcon={<ArrowUpTrayIcon className={classes["icon"]} />}
					variant={EButtonVariant.TEXT}
					color={EButtonColor.PRIMARY}
					fullwidth
					onClick={onUploadPosterButtonClick}
					className={classes["button"]}>
					{I18n.asset.pages.professional_space.tabs.information.poster_container.buttons.deposit}
				</Button>
				<input type="file" name="newPoster" accept={"image/*"} ref={inputRef} onChange={onPosterFileChange} hidden />
			</div>
		</div>
	);
}

export default memo(PosterContainer);
